import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const AFFILIATE_URL = "https://t.deriv.link/?t=P2HUD9J3RRVJ";
export const TELEGRAM_URL = "https://t.me/+hOoracv6C8JkY2Vh";
export const DERIV_CASHIER_URL = "https://home.deriv.com/dashboard";

// Error toast that offers a re-login when the Deriv session is gone.
export const toastApiError = (toast, e, fallback) => {
  const status = e?.response?.status;
  const raw = e?.response?.data?.detail;
  const msg = typeof raw === "string" && raw.length < 160 && !/cloudflare|origin web server/i.test(raw)
    ? raw
    : (!e?.response || status >= 500 ? "Connection to the casino server dropped — please try again" : fallback);
  if (status === 401) toast.error(msg, { id: "auth-401", action: { label: "Login", onClick: () => connectToDeriv() } });
  else toast.error(msg, { id: msg });
};

// Session + the exact account (Real or Demo) the user picked. Trades are only sent with both.
export const tradeAuth = () => {
  const session_id = localStorage.getItem("naevo_session");
  const account_id = localStorage.getItem("naevo_active_account");
  const type = localStorage.getItem("naevo_account_type") === "demo" ? "demo" : "real";
  return session_id && account_id && account_id !== "UNKNOWN" ? { session_id, account_id, type } : null;
};

// Deriv OAuth: use the official legacy endpoint which returns ?acct1=&token1= directly to the
// redirect URL registered on the Deriv app page. PKCE / response_type=code are NOT supported
// here — adding them makes Deriv's consent page hang with no return path.
export const DERIV_APP_ID = "34FdIivnOEbAIu8P6VJcs";
export const AFFILIATE_TOKEN = "P2HUD9J3RRVJ";
// Deriv's realtime WebSocket requires a NUMERIC app_id. Our OAuth client id above is
// alphanumeric and must NEVER be sent to wss://ws.derivws.com — doing so makes
// `authorize` fail and leaves the dashboard stuck on "REFRESHING SESSION…".
// 1089 is Deriv's public default websocket app id and is safe to use for market/account calls.
export const DERIV_WS_APP_ID = (() => {
  const v = (process.env.REACT_APP_DERIV_WS_APP_ID || "1089").toString().trim();
  return /^\d+$/.test(v) ? v : "1089";
})();
let _resolvedAppId = null;
export async function resolveDerivAppId() {
  if (_resolvedAppId) return _resolvedAppId;
  try {
    const r = await api.get("/config");
    _resolvedAppId = String(r.data?.app_id || DERIV_APP_ID);
  } catch {
    _resolvedAppId = DERIV_APP_ID;
  }
  return _resolvedAppId;
}

export function buildConnectUrl(appId = DERIV_APP_ID) {
  // Static fallback href — only used if JS fails. Points at the SAME endpoint the real
  // flow (connectToDeriv) uses: auth.deriv.com/oauth2/auth with trade+account_manage scopes.
  // We can't build a full PKCE URL here (requires Web Crypto), so this href is a graceful
  // "navigate to Deriv so the user isn't stuck" fallback — not expected to actually log in.
  const redirect = typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : "https://naevobot.com/auth/callback";
  const params = new URLSearchParams({
    response_type: "code",
    client_id: appId,
    redirect_uri: redirect,
    scope: "trade account_manage",
  });
  return `https://auth.deriv.com/oauth2/auth?${params.toString()}`;
}

// Exact replica of VTS's working OAuth flow (observed in their production bundle):
//   https://auth.deriv.com/oauth2/auth?response_type=code&client_id=…&redirect_uri=…
//     &scope=trade account_manage&state=…&code_challenge=…&code_challenge_method=S256
// Everything (verifier, challenge, state) is generated in the browser and the verifier
// is kept in sessionStorage so AuthCallback can swap the code for an access_token server-side.
// Doing this frontend-side avoids our backend's 302, which Safari ITP strips cookies on.
export async function connectToDeriv() {
  try {
    const { createPkceChallenge, createState } = await import("./pkce");
    const { verifier, challenge } = await createPkceChallenge();
    const state = createState();
    sessionStorage.setItem("naevo_pkce_verifier", verifier);
    sessionStorage.setItem("naevo_oauth_state", state);
    const clientId = await resolveDerivAppId();
    const redirect = `${window.location.origin}/auth/callback`;
    const url = new URL("https://auth.deriv.com/oauth2/auth");
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("redirect_uri", redirect);
    url.searchParams.set("scope", "trade account_manage");
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    window.location.href = url.toString();
  } catch (e) {
    // If PKCE / crypto fails (very old browser), fall back to the direct legacy URL
    window.location.href = buildConnectUrl();
  }
}
